import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';

import TagManager from '../../../classes/TagManager';
import IconButton from '../../atoms/iconButton/IconButton';
import { ArrowRight } from '../../atoms/icons';
import { TextField } from '../../atoms/textField/TextField';
import { validateEmailSchema } from '../../molecules/forms/utils/validationSchemas';
import { Heading } from '../../typography/heading/Heading';
import { useNostoContext } from '../../../provider/nosto/NostoContext';
import styles from './Footer.module.scss';
import { FORM_TYPE } from '../../molecules/account/accountDropdown/AccountDropdown';
import { useOpenSignForm } from '../../../utils/useOpenSignForm';
import addKlaviyoSubscriber from '../../../utils/addKlaviyoSubscriber';
import PrimaryLoader from '../../atoms/primaryLoader/PrimaryLoader';
import { theme } from './_vars';
import GetCSRFToken from '../../atoms/getCSRFToken/GetCSRFToken';
import InlineMessage from '../../atoms/inlineMessage/InlineMessage';
import CountrySelector from '../../atoms/countySelector';
import { useYupValidationResolver } from '../../../utils/useYupValidationResolver';

export const FooterNewsletterForm = ({
  className = null,
  title = '',
  description = '',
  socialLinks = null,
  loggedIn,
}) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { nostoUpdateCustomer } = useNostoContext();
  const { signFormOpen } = useOpenSignForm();
  const resolver = useYupValidationResolver(validateEmailSchema);
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm({
    resolver,
    mode: 'onSubmit',
  });

  const submitNewsLetter = async ({ email }) => {
    sessionStorage.setItem('newsLetterEmail', email);
    TagManager.newsLetterSubscribe(email);
    nostoUpdateCustomer({ email: email, marketingComms: true });

    const csrf_token = await GetCSRFToken('klaviyo_subscriber_add');
    const data = {
      email,
      csrf_token,
    };

    try {
      setError(null);
      setIsLoading(true);
      const res = await addKlaviyoSubscriber(data);
      if (res?.success) {
        reset();
        signFormOpen(FORM_TYPE.REGISTER);
        return;
      }
      setError(res.message);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      <Heading
        as="p"
        textTransform="uppercase"
        customClasses={{
          common_p: styles.common_p,
        }}
      >
        {title}
      </Heading>
      <p className={styles.newsletterText}>{description}</p>
      {!loggedIn && (
        <>
          <form className={styles.footerForm} onSubmit={handleSubmit(submitNewsLetter)}>
            <TextField
              {...register('email')}
              className={styles.newsletterField}
              customClasses={{
                input: styles.newsletterFieldInput,
              }}
              type="text"
              inputMode="email"
              variant="standard"
              placeholder="Enter email address"
              errorMessage={errors?.email?.message}
              error={!!errors?.email}
            />
            <IconButton
              type="submit"
              ariaLabel="Submit newsletter"
              className={styles.newsletterSubmit}
              iconRegular={() => <ArrowRight width="22" height="22" />}
              iconHover={() => <ArrowRight width="22" height="22" />}
              iconActive={() => <ArrowRight width="22" height="22" />}
              iconLoading={() => (
                <PrimaryLoader mode="segment" size={18} theme={theme === 'Jag' ? 'dark' : 'light'} borderWidth={1} />
              )}
              loading={isLoading}
            />
          </form>
          {error && <InlineMessage type="error" message={error ?? 'An error has occurred. Try later please.'} />}
        </>
      )}
      <div className={clsx(styles.socialLinks, loggedIn && styles.loggedIn)}>
        {socialLinks &&
          Array.from(socialLinks).map((socialLink, index) => {
            if (!socialLink.socialIcon) return null;
            return (
              socialLink.socialIcon &&
              socialLink.socialIcon.media && (
                <a
                  aria-label={socialLink.socialIcon.media.name ?? 'social icon url'}
                  target={socialLink.socialReference?.openInNewWindow ? '_blank' : '_self'}
                  key={index}
                  href={socialLink.socialReference.link}
                  className={styles.link}
                >
                  <svg height={26} width={26}>
                    <image
                      xlinkHref={socialLink.socialIcon.media.file}
                      src={socialLink.socialIcon.media.file}
                      alt={socialLink.socialIcon.media.name ?? 'social icon'}
                    />
                  </svg>
                </a>
              )
            );
          })}
      </div>
      <div className={styles.countrySelectorWrap}>
        <CountrySelector />
      </div>
    </div>
  );
};

FooterNewsletterForm.propTypes = {
  title: PropTypes.string,
  socialLinks: PropTypes.array,
  className: PropTypes.string,
  description: PropTypes.string,
};
